import type { AssignmentSubmissionSchema } from '@repo/schemas/assignment/assignmentSubmission';
import type { UseFormReturn } from 'react-hook-form';

import { useCallback, useEffect, useRef, useState } from 'react';

import { assignmentResolveDraftStore } from './assignmentResolveDraftStore';
import { ASSIGNMENT_RESOLVE_DRAFT_MAX_AGE_MS, ASSIGNMENT_RESOLVE_DRAFT_WRITE_DEBOUNCE_MS } from './useAssignmentResolveForm.type';

interface UseAssignmentResolveDraftArgs {
  draftKey: string;
  form: UseFormReturn<AssignmentSubmissionSchema>;
}

interface UseAssignmentResolveDraftResult {
  attachments: File[];
  setAttachments: (files: File[]) => void;
  isDraftReady: boolean;
  clearDraft: () => void;
}

export function useAssignmentResolveDraft(args: UseAssignmentResolveDraftArgs): UseAssignmentResolveDraftResult {
  const { draftKey, form } = args;

  const [attachments, setAttachmentsState] = useState<File[]>([]);
  const [isDraftReady, setIsDraftReady] = useState<boolean>(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Debounced write of the whole record (body + attachments) under one key.
  const persist = useCallback(
    (nextAttachments: File[]) => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        assignmentResolveDraftStore
          .set(draftKey, {
            body: form.getValues('body') ?? '',
            attachments: nextAttachments,
            updatedAt: Date.now(),
          })
          .catch(() => undefined);
      }, ASSIGNMENT_RESOLVE_DRAFT_WRITE_DEBOUNCE_MS);
    },
    [draftKey, form]
  );

  // Restore once before the form/editor render, so the body seeds Tiptap's
  // initial content and the attachments seed the file selector.
  useEffect(() => {
    let cancelled = false;
    assignmentResolveDraftStore.prune((record) => record.updatedAt < Date.now() - ASSIGNMENT_RESOLVE_DRAFT_MAX_AGE_MS).catch(() => undefined);

    assignmentResolveDraftStore
      .get(draftKey)
      .then((record) => {
        if (cancelled || !record) return;
        form.reset({ body: record.body });
        setAttachmentsState(record.attachments);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsDraftReady(true);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
  }, [draftKey, form]);

  // Persist body edits once restoration has completed.
  useEffect(() => {
    if (!isDraftReady) return;
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: () => persist(attachments),
    });
    return () => unsubscribe();
  }, [form, persist, attachments, isDraftReady]);

  const setAttachments = useCallback(
    (files: File[]) => {
      setAttachmentsState(files);
      if (isDraftReady) persist(files);
    },
    [persist, isDraftReady]
  );

  const clearDraft = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setAttachmentsState([]);
    assignmentResolveDraftStore.delete(draftKey).catch(() => undefined);
  }, [draftKey]);

  return {
    attachments,
    setAttachments,
    isDraftReady,
    clearDraft,
  };
}
